import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2,
  Send,
  Sparkles,
  ShieldCheck,
  HelpCircle,
  Scissors,
  Check,
  X,
  Bot,
  ChevronDown,
  ChevronUp,
  Users,
  AlertTriangle,
  Layers,
  Eye,
  Trash2,
  Layers3,
  RefreshCcw,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { applyStagedEdits } from "@/lib/stagedSectionApply";
import { DiffPreviewModal } from "@/components/DiffPreviewModal";
import { MarkdownMessage } from "@/components/MarkdownMessage";

type Span = { source: "section" | "transcript"; quote: string };

type ProposedChange = {
  target:
    | "sectionContent"
    | "sectionDescription"
    | "sectionExamples"
    | "sectionFormatTemplate"
    | "tagRule"
    | "sectionTags";
  action: "replace" | "remove" | "add";
  rationale?: string;
  newValue?: string;
  exampleIndex?: number;
  ruleId?: string;
  tagId?: string;
  spans?: Span[];
};

type AssistResponse = {
  agent: { kind: "user" | "auto"; id: string | null; name: string };
  templateSection: {
    id: string;
    name: string;
    exampleCount: number;
  } | null;
  sectionRules: {
    id: string;
    tagId?: string;
    tagName: string;
    content: string;
  }[];
  sectionTags?: { id: string; name: string }[];
  availableTags?: { id: string; name: string }[];
  answer: string;
  spans?: Span[];
  proposedChanges: ProposedChange[];
};

type PanelProposal = {
  agentId: string;
  agentName: string;
  change: ProposedChange;
};

type PanelCluster = {
  key: string;
  label: string;
  target: ProposedChange["target"];
  proposals: PanelProposal[];
  consensus: boolean;
  singleReviewer: boolean;
  conflict: boolean;
};

type PanelResponse = {
  agents: { id: string; name: string }[];
  templateSection: AssistResponse["templateSection"];
  sectionRules: AssistResponse["sectionRules"];
  sectionTags?: AssistResponse["sectionTags"];
  availableTags?: AssistResponse["availableTags"];
  perAgent: {
    agentId: string;
    agentName: string;
    answer: string;
    error?: string;
  }[];
  clusters: PanelCluster[];
};

type Turn = {
  id: string;
  messageId?: Id<"chatMessages">;
  role: "user" | "assistant";
  content: string;
  focusQuote?: string;
  agentName?: string;
  agentId?: string | null;
  spans?: Span[];
  proposedChanges?: ProposedChange[];
  appliedChangeIds?: Set<string>;
  dismissedChangeIds?: Set<string>;
  templateSection?: AssistResponse["templateSection"];
  sectionRules?: AssistResponse["sectionRules"];
  sectionTags?: AssistResponse["sectionTags"];
  availableTags?: AssistResponse["availableTags"];
  panel?: PanelResponse;
  appliedClusterKeys?: Set<string>;
  dismissedClusterKeys?: Set<string>;
};

function changeKey(t: Turn, i: number) {
  return `${t.id}-${i}`;
}

function describeChange(c: ProposedChange): string {
  switch (c.target) {
    case "sectionContent":
      return "Rewrite the rendered section content";
    case "sectionDescription":
      return "Rewrite the section description in the template";
    case "sectionFormatTemplate":
      return "Rewrite the section format template";
    case "sectionExamples":
      if (c.action === "add") return "Add a new example to the section";
      if (c.action === "remove")
        return `Remove example ${c.exampleIndex} from the section`;
      return `Replace example ${c.exampleIndex} in the section`;
    case "tagRule":
      if (c.action === "remove") return "Remove a tag-scoped rule";
      if (c.action === "add") return "Add a new tag-scoped rule";
      return "Rewrite a tag-scoped rule";
    case "sectionTags":
      if (c.action === "add") return "Attach a tag to this section";
      if (c.action === "remove") return "Remove a tag from this section";
      return "Change section tags";
  }
}

type TemplateSectionLite = {
  _id: Id<"templateSections">;
  name: string;
  description: string;
  examples: string[];
  adhereToFormatting?: boolean;
  formatTemplate?: string;
  doubleSpaceOutput?: boolean;
  allowAssessment?: boolean;
  tagIds?: Id<"ruleTags">[];
};

// ---- Span resolution & highlight rendering ----

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function findQuoteRange(
  haystack: string,
  quote: string,
): { start: number; end: number } | null {
  const q = (quote || "").trim();
  if (!q || !haystack) return null;
  const pattern = escapeRegex(q).replace(/\s+/g, "\\s+");
  try {
    const re = new RegExp(pattern, "i");
    const m = re.exec(haystack);
    if (!m || m.index === undefined) return null;
    return { start: m.index, end: m.index + m[0].length };
  } catch {
    return null;
  }
}

type Comment = {
  id: string;
  label: string;
  text: string;
  // transcript excerpts the agent provided for this comment, used in popovers on section spans
  transcriptExcerpts: string[];
};

type ResolvedHighlight = {
  start: number;
  end: number;
  commentId: string;
};

function buildCommentsAndHighlights(
  turn: Turn,
  sectionContent: string,
  transcription: string,
): {
  comments: Comment[];
  sectionHighlights: ResolvedHighlight[];
  transcriptHighlights: ResolvedHighlight[];
} {
  const comments: Comment[] = [];
  const sectionHighlights: ResolvedHighlight[] = [];
  const transcriptHighlights: ResolvedHighlight[] = [];

  const pushComment = (
    id: string,
    label: string,
    text: string,
    spans: Span[] | undefined,
  ) => {
    const transcriptExcerpts: string[] = [];
    const sectionSpansForComment: Span[] = [];
    const transcriptSpansForComment: Span[] = [];
    for (const s of spans || []) {
      if (s.source === "transcript") {
        transcriptSpansForComment.push(s);
        const range = findQuoteRange(transcription, s.quote);
        if (range) {
          transcriptExcerpts.push(transcription.slice(range.start, range.end));
        }
      } else {
        sectionSpansForComment.push(s);
      }
    }
    const comment: Comment = { id, label, text, transcriptExcerpts };
    comments.push(comment);
    for (const s of sectionSpansForComment) {
      const r = findQuoteRange(sectionContent, s.quote);
      if (r) sectionHighlights.push({ ...r, commentId: id });
    }
    for (const s of transcriptSpansForComment) {
      const r = findQuoteRange(transcription, s.quote);
      if (r) transcriptHighlights.push({ ...r, commentId: id });
    }
  };

  pushComment("answer", "Answer", turn.content, turn.spans);
  (turn.proposedChanges || []).forEach((c, i) => {
    const text = [describeChange(c), c.rationale].filter(Boolean).join(" — ");
    pushComment(`change-${i}`, `Change ${i + 1}`, text, c.spans);
  });

  // Drop overlapping highlights (keep first by start)
  const dedupe = (arr: ResolvedHighlight[]) => {
    const sorted = [...arr].sort((a, b) => a.start - b.start || a.end - b.end);
    const out: ResolvedHighlight[] = [];
    let lastEnd = -1;
    for (const h of sorted) {
      if (h.start >= lastEnd) {
        out.push(h);
        lastEnd = h.end;
      }
    }
    return out;
  };

  return {
    comments,
    sectionHighlights: dedupe(sectionHighlights),
    transcriptHighlights: dedupe(transcriptHighlights),
  };
}

function HighlightedText({
  text,
  highlights,
  comments,
  source,
  hoveredCommentId,
  setHoveredCommentId,
  onSpanClick,
  testIdPrefix,
}: {
  text: string;
  highlights: ResolvedHighlight[];
  comments: Comment[];
  source: "section" | "transcript";
  hoveredCommentId: string | null;
  setHoveredCommentId: (id: string | null) => void;
  onSpanClick: (commentId: string) => void;
  testIdPrefix: string;
}) {
  if (!text) {
    return (
      <span className="italic text-muted-foreground text-xs">(empty)</span>
    );
  }
  if (highlights.length === 0) {
    return <span className="whitespace-pre-wrap">{text}</span>;
  }
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  highlights.forEach((h, i) => {
    if (cursor < h.start) {
      nodes.push(
        <span key={`t-${i}`} className="whitespace-pre-wrap">
          {text.slice(cursor, h.start)}
        </span>,
      );
    }
    const comment = comments.find((c) => c.id === h.commentId);
    const isHovered = hoveredCommentId === h.commentId;
    nodes.push(
      <Popover key={`h-${i}`}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onMouseEnter={() => setHoveredCommentId(h.commentId)}
            onMouseLeave={() => setHoveredCommentId(null)}
            onClick={() => onSpanClick(h.commentId)}
            className={`inline whitespace-pre-wrap text-left bg-yellow-200/70 dark:bg-yellow-700/30 underline decoration-yellow-600 decoration-2 underline-offset-2 px-0.5 rounded-sm hover:bg-yellow-300/80 dark:hover:bg-yellow-700/50 transition-colors ${
              isHovered
                ? "ring-2 ring-yellow-500 dark:ring-yellow-400 bg-yellow-300/80 dark:bg-yellow-700/60"
                : ""
            }`}
            data-testid={`${testIdPrefix}-${i}`}
            data-comment-id={h.commentId}
          >
            {text.slice(h.start, h.end)}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 text-xs" align="start">
          <div className="font-semibold mb-1">
            {comment?.label || "Agent comment"}
          </div>
          <div className="whitespace-pre-wrap">
            {comment?.text || "(no comment)"}
          </div>
          {source === "section" && comment && (
            <div className="mt-2 pt-2 border-t border-border">
              <div className="font-semibold mb-1">Transcript excerpt</div>
              {comment.transcriptExcerpts.length > 0 ? (
                comment.transcriptExcerpts.map((ex, k) => (
                  <div key={k} className="italic">
                    “{ex}”
                  </div>
                ))
              ) : (
                <div className="italic text-muted-foreground">
                  No source found in transcript.
                </div>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>,
    );
    cursor = h.end;
  });
  if (cursor < text.length) {
    nodes.push(
      <span key="t-end" className="whitespace-pre-wrap">
        {text.slice(cursor)}
      </span>,
    );
  }
  return <>{nodes}</>;
}

export function SectionAssistantHeader({
  userId,
  sectionTitle,
  selectedAgentIds,
  setSelectedAgentIds,
  className,
}: {
  userId: Id<"users">;
  sectionTitle: string;
  selectedAgentIds: string[];
  setSelectedAgentIds: React.Dispatch<React.SetStateAction<string[]>>;
  className?: string;
}) {
  const agents = useQuery(api.agents.list, { userId });
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const userAgentOptions = useMemo(
    () =>
      (agents || []).map((a) => ({
        id: String(a._id),
        name: a.name,
        description: a.description,
      })),
    [agents],
  );
  return (
    <div className={className ?? "p-4 pb-3 border-b border-border shrink-0"}>
      <div className="flex items-center gap-2 font-semibold text-sm">
        <Sparkles className="w-4 h-4 text-primary" />
        Section Assistant — {sectionTitle}
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        Ask about the “{sectionTitle}” section. The assistant can read the
        transcript, notes, section content, description, examples, formatting,
        and tag-scoped rules, and can propose edits for you to apply.
      </div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <Bot className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Agent:</span>
        <Popover open={agentPickerOpen} onOpenChange={setAgentPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs justify-between gap-2 min-w-[14rem]"
              data-testid="button-section-assistant-agent-picker"
            >
              <span className="truncate text-left">
                {selectedAgentIds.length === 0
                  ? "Auto-pick best agent"
                  : selectedAgentIds.length === 1
                    ? userAgentOptions.find(
                        (a) => a.id === selectedAgentIds[0],
                      )?.name || "1 agent"
                    : `Panel: ${selectedAgentIds.length} agents`}
              </span>
              <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2" align="start">
            <div className="text-xs text-muted-foreground px-2 py-1">
              Select up to 3 agents. Pick 2 or more to run a panel review.
            </div>
            <div className="max-h-64 overflow-auto">
              <button
                type="button"
                className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover-elevate text-sm"
                onClick={() => setSelectedAgentIds([])}
                data-testid="button-agent-auto"
              >
                <Checkbox
                  checked={selectedAgentIds.length === 0}
                  className="pointer-events-none"
                />
                <span>Auto-pick best agent</span>
              </button>
              {userAgentOptions.map((a) => {
                const checked = selectedAgentIds.includes(a.id);
                const atMax = selectedAgentIds.length >= 3 && !checked;
                return (
                  <button
                    key={a.id}
                    type="button"
                    className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover-elevate text-sm disabled:opacity-50"
                    disabled={atMax}
                    onClick={() => {
                      setSelectedAgentIds((prev) =>
                        prev.includes(a.id)
                          ? prev.filter((x) => x !== a.id)
                          : prev.length >= 3
                            ? prev
                            : [...prev, a.id],
                      );
                    }}
                    data-testid={`button-agent-toggle-${a.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      className="pointer-events-none"
                    />
                    <span className="truncate">{a.name}</span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        {selectedAgentIds.length >= 2 && (
          <Badge variant="secondary" className="text-[10px]">
            <Users className="w-3 h-3 mr-1" />
            Panel mode
          </Badge>
        )}
      </div>
    </div>
  );
}

export function SectionAssistant({
  chatId,
  sessionId,
  templateId,
  userId,
  sectionTitle,
  sectionContent,
  transcription,
  templateSections,
  onApplySectionContent,
  initialFocusQuote,
  onConsumeInitialFocusQuote,
  selectedAgentIds: selectedAgentIdsProp,
  setSelectedAgentIds: setSelectedAgentIdsProp,
  hideHeader,
}: {
  chatId: Id<"chats">;
  sessionId: Id<"recordingSessions">;
  templateId: Id<"templates">;
  userId: Id<"users">;
  sectionTitle: string;
  sectionContent: string;
  transcription?: string;
  templateSections?: TemplateSectionLite[];
  onApplySectionContent: (
    newValue: string,
    opts?: {
      provenance?: { source: "user" | "agent"; agentId?: Id<"agents"> };
      summaryOverride?: string;
    },
  ) => Promise<void> | void;
  initialFocusQuote?: string | null;
  onConsumeInitialFocusQuote?: () => void;
  selectedAgentIds?: string[];
  setSelectedAgentIds?: React.Dispatch<React.SetStateAction<string[]>>;
  hideHeader?: boolean;
}) {
  const { toast } = useToast();
  const agents = useQuery(api.agents.list, { userId });
  const updateSection = useMutation(api.templates.updateSection);
  const addExampleToSection = useMutation(api.templates.addExampleToSection);
  const updateRule = useMutation(api.globalRules.update);
  const removeRule = useMutation(api.globalRules.remove);
  const createRule = useMutation(api.globalRules.create);
  const addMessage = useMutation(api.chats.addMessage);
  const applyEditDoc = useMutation(api.recordingSessions.applySectionContentEdit);
  const persistedMessages = useQuery(api.chats.getMessages, {
    chatId,
    userId,
  });
  const session = useQuery(api.recordingSessions.get, { id: sessionId, userId });
  const globalRulesList = useQuery(api.globalRules.list, { userId });

  const activeTemplateSection: TemplateSectionLite | undefined = useMemo(() => {
    if (!templateSections) return undefined;
    const lower = sectionTitle.trim().toLowerCase();
    return templateSections.find((s) => s.name.trim().toLowerCase() === lower);
  }, [templateSections, sectionTitle]);

  const stagedRow = useQuery(
    api.stagedSectionChanges.getForSection,
    activeTemplateSection
      ? {
          userId,
          sessionId,
          templateSectionId: activeTemplateSection._id,
        }
      : "skip",
  );
  const stageEditMut = useMutation(api.stagedSectionChanges.stageEdit);
  const removeStagedEditMut = useMutation(api.stagedSectionChanges.removeEdit);
  const clearStagedMut = useMutation(api.stagedSectionChanges.clear);
  const setPreviewMut = useMutation(api.stagedSectionChanges.setPreview);
  const clearPreviewMut = useMutation(api.stagedSectionChanges.clearPreview);

  const [stagingKey, setStagingKey] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isApplyingStaged, setIsApplyingStaged] = useState(false);
  const [stagedItemErrors, setStagedItemErrors] = useState<
    Record<string, string>
  >({});
  const [trayCollapsed, setTrayCollapsed] = useState(false);

  const [internalSelectedAgentIds, setInternalSelectedAgentIds] = useState<
    string[]
  >([]);
  const selectedAgentIds = selectedAgentIdsProp ?? internalSelectedAgentIds;
  const setSelectedAgentIds =
    setSelectedAgentIdsProp ?? setInternalSelectedAgentIds;
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [conflictChooser, setConflictChooser] = useState<{
    turnIdx: number;
    clusterKey: string;
  } | null>(null);
  const [input, setInput] = useState("");
  const [presetParamSentence, setPresetParamSentence] = useState("");
  const [askingPreset, setAskingPreset] = useState<
    null | "conflicts" | "why-sentence" | "simplify"
  >(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [applyingKey, setApplyingKey] = useState<string | null>(null);
  const [pendingApply, setPendingApply] = useState<
    | {
        kind: "single";
        turnIdx: number;
        changeIdx: number;
        label: string;
        oldValue: string;
        newValue: string;
        intent: "apply" | "review";
      }
    | {
        kind: "panel";
        turnIdx: number;
        clusterKey: string;
        proposal: PanelProposal;
        label: string;
        oldValue: string;
        newValue: string;
        intent: "apply" | "review";
      }
    | null
  >(null);
  const [scope, setScope] = useState<string | null>(null);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [baselineSectionContent, setBaselineSectionContent] = useState<
    string | null
  >(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const commentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Reset when chat changes
  const hydratedChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (hydratedChatIdRef.current !== String(chatId)) {
      hydratedChatIdRef.current = String(chatId);
      setTurns([]);
      setInput("");
      setPresetParamSentence("");
      setAskingPreset(null);
      setPendingApply(null);
      setScope(null);
      setActiveTurnId(null);
      setHoveredCommentId(null);
      setBaselineSectionContent(sectionContent);
      setConflictChooser(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // Hydrate turns from persisted chat messages on first load
  const hydratedFromMessagesRef = useRef<string | null>(null);
  useEffect(() => {
    if (!persistedMessages) return;
    if (hydratedFromMessagesRef.current === String(chatId)) return;
    if (persistedMessages.length === 0) {
      hydratedFromMessagesRef.current = String(chatId);
      return;
    }
    type SerializedTurnPayload = {
      focusQuote?: string;
      agentName?: string;
      agentId?: string | null;
      spans?: Span[];
      proposedChanges?: ProposedChange[];
      templateSection?: AssistResponse["templateSection"];
      sectionRules?: AssistResponse["sectionRules"];
      sectionTags?: AssistResponse["sectionTags"];
      availableTags?: AssistResponse["availableTags"];
      panel?: PanelResponse;
      appliedChangeIds?: string[];
      dismissedChangeIds?: string[];
      appliedClusterKeys?: string[];
      dismissedClusterKeys?: string[];
    };
    const asStringArray = (v: unknown): string[] =>
      Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
    const restored: Turn[] = persistedMessages.map((m) => {
      let parsed: SerializedTurnPayload = {};
      if (m.payload) {
        try {
          const raw: unknown = JSON.parse(m.payload);
          if (raw && typeof raw === "object") {
            parsed = raw as SerializedTurnPayload;
          }
        } catch {}
      }
      const t: Turn = {
        id: m._id,
        messageId: m._id,
        role: m.role,
        content: m.content,
        focusQuote: parsed.focusQuote,
        agentName: parsed.agentName,
        agentId: parsed.agentId ?? null,
        spans: parsed.spans,
        proposedChanges: parsed.proposedChanges,
        templateSection: parsed.templateSection,
        sectionRules: parsed.sectionRules,
        sectionTags: parsed.sectionTags,
        availableTags: parsed.availableTags,
        panel: parsed.panel,
        appliedChangeIds: new Set<string>(asStringArray(parsed.appliedChangeIds)),
        dismissedChangeIds: new Set<string>(asStringArray(parsed.dismissedChangeIds)),
        appliedClusterKeys: new Set<string>(asStringArray(parsed.appliedClusterKeys)),
        dismissedClusterKeys: new Set<string>(asStringArray(parsed.dismissedClusterKeys)),
      };
      return t;
    });
    setTurns(restored);
    hydratedFromMessagesRef.current = String(chatId);
  }, [persistedMessages, chatId]);

  // Pick up initial focus quote when assistant mounts with a selection
  const consumedInitialFocusRef = useRef(false);
  useEffect(() => {
    if (consumedInitialFocusRef.current) return;
    if (initialFocusQuote && initialFocusQuote.trim()) {
      setScope(initialFocusQuote.trim());
      consumedInitialFocusRef.current = true;
      onConsumeInitialFocusQuote?.();
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [initialFocusQuote, onConsumeInitialFocusQuote]);

  // When section content changes externally (apply, user edit), clear highlights.
  useEffect(() => {
    if (baselineSectionContent === null) return;
    if (sectionContent !== baselineSectionContent) {
      setActiveTurnId(null);
      setBaselineSectionContent(sectionContent);
    }
  }, [sectionContent, baselineSectionContent]);

  useEffect(() => {
    const inner = scrollRef.current;
    if (!inner) return;
    const lastChild = inner.lastElementChild as HTMLElement | null;
    if (!lastChild) return;
    // The scrollable element is the Radix ScrollArea Viewport, not the
    // content div itself. Setting scrollTop on the content div is a no-op
    // because it isn't the overflow container. Scrolling the last child
    // into view works regardless of which ancestor actually owns the
    // overflow.
    const run = () => {
      lastChild.scrollIntoView({ block: "end", inline: "nearest" });
    };
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, [turns, isSending, isPreviewing, previewError, stagedRow?.previewContent, stagedRow?.previewGeneratedAt]);

  // Clear in-thread preview error when staged edits go away (apply/discard).
  useEffect(() => {
    if (!stagedRow || (stagedRow.edits || []).length === 0) {
      setPreviewError(null);
    }
  }, [stagedRow]);

  // Clear in-thread preview error when the active staged context changes
  // (different section/staged row) so stale errors don't carry over.
  useEffect(() => {
    setPreviewError(null);
  }, [activeTemplateSection?._id, stagedRow?._id]);

  const activeTurn = useMemo(
    () =>
      activeTurnId ? turns.find((t) => t.id === activeTurnId) || null : null,
    [activeTurnId, turns],
  );

  const { comments, sectionHighlights, transcriptHighlights } = useMemo(() => {
    if (!activeTurn) {
      return {
        comments: [] as Comment[],
        sectionHighlights: [] as ResolvedHighlight[],
        transcriptHighlights: [] as ResolvedHighlight[],
      };
    }
    return buildCommentsAndHighlights(
      activeTurn,
      sectionContent,
      transcription || "",
    );
  }, [activeTurn, sectionContent, transcription]);

  const handleSpanClick = (commentId: string) => {
    const el = commentRefs.current.get(commentId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Briefly emphasize the comment
      el.classList.add("ring-2", "ring-yellow-500");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-yellow-500");
      }, 1200);
    }
  };

  function serializeTurnPayload(t: Turn): string {
    const obj: Record<string, unknown> = {};
    if (t.focusQuote) obj.focusQuote = t.focusQuote;
    if (t.agentName) obj.agentName = t.agentName;
    if (t.agentId !== undefined) obj.agentId = t.agentId;
    if (t.spans) obj.spans = t.spans;
    if (t.proposedChanges) obj.proposedChanges = t.proposedChanges;
    if (t.templateSection) obj.templateSection = t.templateSection;
    if (t.sectionRules) obj.sectionRules = t.sectionRules;
    if (t.sectionTags) obj.sectionTags = t.sectionTags;
    if (t.availableTags) obj.availableTags = t.availableTags;
    if (t.panel) obj.panel = t.panel;
    if (t.appliedChangeIds && t.appliedChangeIds.size > 0)
      obj.appliedChangeIds = Array.from(t.appliedChangeIds);
    if (t.dismissedChangeIds && t.dismissedChangeIds.size > 0)
      obj.dismissedChangeIds = Array.from(t.dismissedChangeIds);
    if (t.appliedClusterKeys && t.appliedClusterKeys.size > 0)
      obj.appliedClusterKeys = Array.from(t.appliedClusterKeys);
    if (t.dismissedClusterKeys && t.dismissedClusterKeys.size > 0)
      obj.dismissedClusterKeys = Array.from(t.dismissedClusterKeys);
    return JSON.stringify(obj);
  }

  const updateMessagePayload = useMutation(api.chats.updateMessagePayload);

  async function persistTurn(t: Turn): Promise<Id<"chatMessages"> | null> {
    try {
      const msgId = await addMessage({
        chatId,
        userId,
        role: t.role,
        content: t.content,
        payload: serializeTurnPayload(t),
      });
      setTurns((prev) =>
        prev.map((x) => (x.id === t.id ? { ...x, messageId: msgId } : x)),
      );
      return msgId;
    } catch (err) {
      console.error("Failed to persist section chat turn", err);
      return null;
    }
  }

  async function persistTurnUpdate(turn: Turn) {
    if (!turn.messageId) return;
    try {
      await updateMessagePayload({
        id: turn.messageId,
        userId,
        payload: serializeTurnPayload(turn),
      });
    } catch (err) {
      console.error("Failed to update section chat turn payload", err);
    }
  }

  function updateTurnAndPersist(
    turnIdx: number,
    updater: (t: Turn) => Turn,
  ) {
    setTurns((prev) => {
      const next = prev.map((t, i) => (i === turnIdx ? updater(t) : t));
      const updated = next[turnIdx];
      if (updated) void persistTurnUpdate(updated);
      return next;
    });
  }

  async function send(
    presetAction?: "conflicts" | "why-sentence" | "simplify",
    presetParam?: string,
    overrideText?: string,
  ) {
    if (isSending) return;
    const question =
      typeof overrideText === "string" ? overrideText.trim() : input.trim();
    if (!presetAction && !question) return;

    const userTurnContent = presetAction
      ? presetAction === "conflicts"
        ? "Check the prompts for conflicts."
        : presetAction === "why-sentence"
          ? presetParam
            ? `Why was this sentence included? "${presetParam}"`
            : "Walk through each sentence and explain why it was included."
          : "Should we simplify or consolidate the section prompts?"
      : question;

    const currentScope = scope;
    const userTurn: Turn = {
      id: `u-${Date.now()}`,
      role: "user",
      content: question
        ? presetAction
          ? `${userTurnContent}\n\n${question}`
          : question
        : userTurnContent,
      focusQuote: currentScope || undefined,
    };
    void persistTurn(userTurn);

    const summarizeProposedChanges = (changes: ProposedChange[]): string => {
      const lines: string[] = [];
      changes.forEach((c, i) => {
        let target = "";
        switch (c.target) {
          case "sectionContent":
            target = "rendered section content";
            break;
          case "sectionDescription":
            target = "template section description";
            break;
          case "sectionFormatTemplate":
            target = "template format template";
            break;
          case "sectionExamples":
            target =
              c.action === "add"
                ? "template examples (new example)"
                : `template example #${c.exampleIndex ?? "?"}`;
            break;
          case "tagRule":
            if (c.action === "add") {
              target = `new tag-scoped rule (tag ${c.tagId ?? "?"})`;
            } else {
              target = `tag-scoped rule (id ${c.ruleId ?? "?"})`;
            }
            break;
          case "sectionTags":
            target =
              c.action === "add"
                ? `attach tag ${c.tagId ?? "?"} to section`
                : `remove tag ${c.tagId ?? "?"} from section`;
            break;
        }
        const excerpt =
          typeof c.newValue === "string" && c.newValue.length > 0
            ? c.newValue.length > 240
              ? `${c.newValue.slice(0, 240)}…`
              : c.newValue
            : "(no new value)";
        const rationaleText = c.rationale
          ? c.rationale.length > 200
            ? `${c.rationale.slice(0, 200)}…`
            : c.rationale
          : "";
        const rationale = rationaleText ? ` — rationale: ${rationaleText}` : "";
        lines.push(
          `${i + 1}. [${c.action}] ${target}${rationale}\n   newValue: ${excerpt}`,
        );
      });
      return lines.join("\n");
    };

    const summarizePanelChanges = (panel: PanelResponse): string => {
      const lines: string[] = [];
      panel.clusters.forEach((cl, i) => {
        const props = cl.proposals
          .map((p) => {
            const c = p.change;
            const excerpt =
              typeof c.newValue === "string" && c.newValue.length > 0
                ? c.newValue.length > 200
                  ? `${c.newValue.slice(0, 200)}…`
                  : c.newValue
                : "(no new value)";
            return `   - ${p.agentName} [${c.action}]: ${excerpt}`;
          })
          .join("\n");
        lines.push(`${i + 1}. ${cl.label} — ${cl.target}\n${props}`);
      });
      return lines.join("\n");
    };

    const history = turns
      .filter((t) => !!t.content || (t.proposedChanges && t.proposedChanges.length > 0) || (t.panel && t.panel.clusters.length > 0))
      .map((t) => {
        if (t.role === "user" && t.focusQuote) {
          return {
            role: t.role,
            content: `[Focused on quote: "${t.focusQuote}"]\n\n${t.content}`,
          };
        }
        if (t.role === "assistant") {
          let content = t.content;
          if (t.proposedChanges && t.proposedChanges.length > 0) {
            content = `${content}\n\nProposed changes from this turn (these are edits I, the assistant, proposed — own them when asked):\n${summarizeProposedChanges(t.proposedChanges)}`;
          }
          if (t.panel && t.panel.clusters.length > 0) {
            content = `${content}\n\nProposed changes from this panel turn (each cluster lists every reviewer's version — each reviewer owns their own entry):\n${summarizePanelChanges(t.panel)}`;
          }
          return { role: t.role, content };
        }
        return { role: t.role, content: t.content };
      });

    setTurns((prev) => [...prev, userTurn]);
    if (typeof overrideText !== "string") {
      setInput("");
    }
    setAskingPreset(null);
    setPresetParamSentence("");
    setScope(null);
    setIsSending(true);
    // Clear highlights at the start of a new turn
    setActiveTurnId(null);
    setHoveredCommentId(null);

    const isPanel = selectedAgentIds.length >= 2;

    try {
      if (isPanel) {
        const res = await apiRequest("POST", "/api/section-assist/panel", {
          sessionId,
          templateId,
          sectionTitle,
          sectionContent,
          question: question || undefined,
          focusQuote: currentScope || undefined,
          presetAction,
          presetParam,
          agentIds: selectedAgentIds,
          history,
        });
        const data = (await res.json()) as PanelResponse;
        const summary = data.perAgent
          .map(
            (a) =>
              `${a.agentName}: ${a.error ? `(error: ${a.error})` : a.answer || "(no answer)"}`,
          )
          .join("\n\n");
        const aTurn: Turn = {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: summary,
          agentId: null,
          proposedChanges: [],
          appliedChangeIds: new Set(),
          dismissedChangeIds: new Set(),
          templateSection: data.templateSection,
          sectionRules: data.sectionRules,
          sectionTags: data.sectionTags,
          availableTags: data.availableTags,
          panel: data,
          appliedClusterKeys: new Set(),
          dismissedClusterKeys: new Set(),
        };
        setTurns((prev) => [...prev, aTurn]);
        void persistTurn(aTurn);
      } else {
        const res = await apiRequest("POST", "/api/section-assist", {
          sessionId,
          templateId,
          sectionTitle,
          sectionContent,
          question: question || undefined,
          focusQuote: currentScope || undefined,
          presetAction,
          presetParam,
          agentId:
            selectedAgentIds.length === 1 ? selectedAgentIds[0] : undefined,
          history,
        });
        const data = (await res.json()) as AssistResponse;
        const aTurn: Turn = {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: data.answer || "(no answer)",
          agentName: data.agent?.name,
          agentId: data.agent?.id ?? null,
          spans: data.spans || [],
          proposedChanges: data.proposedChanges || [],
          appliedChangeIds: new Set(),
          dismissedChangeIds: new Set(),
          templateSection: data.templateSection,
          sectionRules: data.sectionRules,
          sectionTags: data.sectionTags,
          availableTags: data.availableTags,
        };
        setTurns((prev) => [...prev, aTurn]);
        void persistTurn(aTurn);
        setActiveTurnId(aTurn.id);
        setBaselineSectionContent(sectionContent);
      }
    } catch (err: any) {
      let message = err?.message || "Failed to get a response";
      if (err?.response) {
        try {
          const j = await err.response.json();
          if (j?.error) message = j.error;
        } catch {}
      }
      toast({
        title: "Assistant failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  }

  function resolveDiffTarget(
    turn: Turn,
    change: ProposedChange,
  ): { label: string; oldValue: string; newValue: string } | null {
    if (change.target === "sectionContent") {
      return {
        label: "Section content",
        oldValue: sectionContent || "",
        newValue: typeof change.newValue === "string" ? change.newValue : "",
      };
    }
    if (change.target === "sectionTags") {
      const tagId = String(change.tagId || "");
      const fromAvailable = turn.availableTags?.find((t) => t.id === tagId);
      const fromAttached = turn.sectionTags?.find((t) => t.id === tagId);
      const tagName =
        fromAvailable?.name || fromAttached?.name || tagId || "(unknown tag)";
      if (change.action === "add") {
        return {
          label: `Attach tag: ${tagName}`,
          oldValue: "(not attached)",
          newValue: `Tag "${tagName}" attached to this section`,
        };
      }
      if (change.action === "remove") {
        return {
          label: `Remove tag: ${tagName}`,
          oldValue: `Tag "${tagName}" attached to this section`,
          newValue: "(not attached)",
        };
      }
      return null;
    }
    if (change.target === "tagRule") {
      if (change.action === "add") {
        const tag = turn.sectionTags?.find((t) => t.id === change.tagId);
        const tagName = tag?.name || "rule";
        return {
          label: `New global rule: ${tagName}`,
          oldValue: "",
          newValue:
            typeof change.newValue === "string" ? change.newValue : "",
        };
      }
      const rule = turn.sectionRules?.find((r) => r.id === change.ruleId);
      const tagName = rule?.tagName || "rule";
      const oldValue = rule?.content || "";
      if (change.action === "remove") {
        return {
          label: `Remove global rule: ${tagName}`,
          oldValue,
          newValue: "",
        };
      }
      return {
        label: `Global rule: ${tagName}`,
        oldValue,
        newValue: typeof change.newValue === "string" ? change.newValue : "",
      };
    }
    const ts = turn.templateSection;
    if (!ts) return null;
    const sec = templateSections?.find((s) => String(s._id) === ts.id);
    if (!sec) return null;
    if (change.target === "sectionDescription") {
      return {
        label: "Template description",
        oldValue: sec.description || "",
        newValue: typeof change.newValue === "string" ? change.newValue : "",
      };
    }
    if (change.target === "sectionFormatTemplate") {
      return {
        label: "Section format template",
        oldValue: sec.formatTemplate || "",
        newValue: typeof change.newValue === "string" ? change.newValue : "",
      };
    }
    // sectionExamples
    const examples = sec.examples || [];
    if (change.action === "add") {
      return {
        label: `Examples[${examples.length + 1}] (new)`,
        oldValue: "",
        newValue: typeof change.newValue === "string" ? change.newValue : "",
      };
    }
    const idx =
      typeof change.exampleIndex === "number" ? change.exampleIndex - 1 : -1;
    const existing = idx >= 0 && idx < examples.length ? examples[idx] : "";
    if (change.action === "remove") {
      return {
        label: `Remove Examples[${change.exampleIndex}]`,
        oldValue: existing,
        newValue: "",
      };
    }
    return {
      label: `Examples[${change.exampleIndex}]`,
      oldValue: existing,
      newValue: typeof change.newValue === "string" ? change.newValue : "",
    };
  }

  function requestApply(turnIdx: number, changeIdx: number) {
    const turn = turns[turnIdx];
    const change = turn?.proposedChanges?.[changeIdx];
    if (!change) return;
    const needsTemplateSection =
      change.target === "sectionDescription" ||
      change.target === "sectionExamples" ||
      change.target === "sectionFormatTemplate" ||
      change.target === "sectionTags";
    if (needsTemplateSection && !turn.templateSection) {
      toast({
        title: "Cannot apply",
        description:
          "No matching template section was found, so this edit cannot be applied automatically.",
        variant: "destructive",
      });
      return;
    }
    const target = resolveDiffTarget(turn, change);
    if (!target) {
      toast({
        title: "Cannot preview change",
        description: "Could not locate the current value to compare against.",
        variant: "destructive",
      });
      return;
    }
    setPendingApply({
      kind: "single",
      turnIdx,
      changeIdx,
      label: target.label,
      oldValue: target.oldValue,
      newValue: target.newValue,
      intent: "apply",
    });
  }

  function requestPreview(turnIdx: number, changeIdx: number) {
    const turn = turns[turnIdx];
    const change = turn?.proposedChanges?.[changeIdx];
    if (!change) return;
    const target = resolveDiffTarget(turn, change);
    if (!target) {
      toast({
        title: "Cannot preview change",
        description: "Could not locate the current value to compare against.",
        variant: "destructive",
      });
      return;
    }
    setPendingApply({
      kind: "single",
      turnIdx,
      changeIdx,
      label: target.label,
      oldValue: target.oldValue,
      newValue: target.newValue,
      intent: "review",
    });
  }

  function requestPanelPreview(
    turnIdx: number,
    clusterKey: string,
    proposal: PanelProposal,
  ) {
    const turn = turns[turnIdx];
    if (!turn) return;
    const target = resolveDiffTarget(turn, proposal.change);
    if (!target) {
      toast({
        title: "Cannot preview change",
        description: "Could not locate the current value to compare against.",
        variant: "destructive",
      });
      return;
    }
    setPendingApply({
      kind: "panel",
      turnIdx,
      clusterKey,
      proposal,
      label: target.label,
      oldValue: target.oldValue,
      newValue: target.newValue,
      intent: "review",
    });
  }

  async function executeApply(turnIdx: number, changeIdx: number) {
    const turn = turns[turnIdx];
    const change = turn.proposedChanges?.[changeIdx];
    if (!change) return;
    const needsTemplateSection =
      change.target === "sectionDescription" ||
      change.target === "sectionExamples" ||
      change.target === "sectionFormatTemplate" ||
      change.target === "sectionTags";
    if (needsTemplateSection && !turn.templateSection) {
      toast({
        title: "Cannot apply",
        description:
          "No matching template section was found, so this edit cannot be applied automatically.",
        variant: "destructive",
      });
      return;
    }
    const key = changeKey(turn, changeIdx);
    setApplyingKey(key);
    const provenance = {
      source: "agent" as const,
      ...(turn.agentId ? { agentId: turn.agentId as Id<"agents"> } : {}),
    };
    try {
      if (change.target === "sectionContent") {
        if (typeof change.newValue !== "string")
          throw new Error("Missing new content");
        await onApplySectionContent(change.newValue, { provenance });
      } else if (
        change.target === "sectionDescription" ||
        change.target === "sectionFormatTemplate" ||
        change.target === "sectionExamples" ||
        change.target === "sectionTags"
      ) {
        const ts = turn.templateSection!;
        const sec = templateSections?.find((s) => String(s._id) === ts.id);
        if (!sec) throw new Error("Section not found");
        let nextDescription = sec.description;
        let nextExamples = [...(sec.examples || [])];
        let nextFormat = sec.formatTemplate;
        let nextTagIds: Id<"ruleTags">[] = [...((sec.tagIds as Id<"ruleTags">[]) || [])];
        if (change.target === "sectionDescription") {
          if (typeof change.newValue !== "string")
            throw new Error("Missing new description");
          nextDescription = change.newValue;
        } else if (change.target === "sectionFormatTemplate") {
          if (typeof change.newValue !== "string")
            throw new Error("Missing new format template");
          nextFormat = change.newValue;
        } else if (change.target === "sectionTags") {
          if (!change.tagId) throw new Error("Missing tag id");
          const tid = String(change.tagId);
          const currentIds = nextTagIds.map((id) => String(id));
          if (change.action === "add") {
            if (currentIds.includes(tid))
              throw new Error("Tag already attached to section");
            nextTagIds.push(change.tagId as Id<"ruleTags">);
          } else if (change.action === "remove") {
            if (!currentIds.includes(tid))
              throw new Error("Tag is not attached to section");
            nextTagIds = nextTagIds.filter((id) => String(id) !== tid);
          } else {
            throw new Error("Unsupported action for sectionTags");
          }
        } else {
          if (change.action === "add") {
            if (typeof change.newValue !== "string")
              throw new Error("Missing example text");
            nextExamples.push(change.newValue);
          } else if (change.action === "remove") {
            if (typeof change.exampleIndex !== "number")
              throw new Error("Missing example index");
            const removeIdx = change.exampleIndex - 1;
            if (removeIdx < 0 || removeIdx >= nextExamples.length)
              throw new Error("Example index out of range");
            nextExamples = nextExamples.filter((_, i) => i !== removeIdx);
          } else {
            if (
              typeof change.exampleIndex !== "number" ||
              typeof change.newValue !== "string"
            )
              throw new Error("Missing example index or text");
            const idx = change.exampleIndex - 1;
            if (idx < 0 || idx >= nextExamples.length)
              throw new Error("Example index out of range");
            nextExamples[idx] = change.newValue;
          }
        }
        await updateSection({
          id: ts.id as Id<"templateSections">,
          userId,
          name: sec.name,
          description: nextDescription,
          examples: nextExamples,
          adhereToFormatting: sec.adhereToFormatting,
          formatTemplate: nextFormat,
          doubleSpaceOutput: sec.doubleSpaceOutput,
          allowAssessment: sec.allowAssessment,
          tagIds: nextTagIds,
          provenance,
        });
        void addExampleToSection;
      } else if (change.target === "tagRule") {
        if (change.action === "add") {
          if (!change.tagId) throw new Error("Missing tag id for new rule");
          if (typeof change.newValue !== "string" || !change.newValue.trim())
            throw new Error("Missing new rule content");
          await createRule({
            userId,
            content: change.newValue,
            tagId: change.tagId as Id<"ruleTags">,
            provenance,
          });
        } else if (change.action === "remove") {
          if (!change.ruleId) throw new Error("Missing rule id");
          await removeRule({
            id: change.ruleId as Id<"globalRules">,
            userId,
          });
        } else {
          if (!change.ruleId) throw new Error("Missing rule id");
          if (typeof change.newValue !== "string")
            throw new Error("Missing new rule content");
          await updateRule({
            id: change.ruleId as Id<"globalRules">,
            userId,
            content: change.newValue,
            provenance,
          });
        }
      }
      updateTurnAndPersist(turnIdx, (t) => ({
        ...t,
        appliedChangeIds: new Set([
          ...Array.from(t.appliedChangeIds || []),
          String(changeIdx),
        ]),
      }));
      // Clear highlights after a successful apply
      setActiveTurnId(null);
      setHoveredCommentId(null);
      toast({ title: "Change applied" });
    } catch (err: any) {
      toast({
        title: "Could not apply change",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setApplyingKey(null);
    }
  }

  async function applyPanelChange(
    turnIdx: number,
    clusterKey: string,
    proposal: PanelProposal,
  ) {
    const turn = turns[turnIdx];
    if (!turn?.panel) return;
    const change = proposal.change;
    const needsTemplateSection = change.target !== "sectionContent";
    if (needsTemplateSection && !turn.templateSection) {
      toast({
        title: "Cannot apply",
        description:
          "No matching template section was found, so this edit cannot be applied automatically.",
        variant: "destructive",
      });
      return;
    }
    const key = `panel-${turn.id}-${clusterKey}-${proposal.agentId}`;
    setApplyingKey(key);
    const provenance = {
      source: "agent" as const,
      agentId: proposal.agentId as Id<"agents">,
    };
    try {
      if (change.target === "sectionContent") {
        if (typeof change.newValue !== "string")
          throw new Error("Missing new content");
        await onApplySectionContent(change.newValue, { provenance });
      } else if (
        change.target === "sectionDescription" ||
        change.target === "sectionFormatTemplate" ||
        change.target === "sectionExamples" ||
        change.target === "sectionTags"
      ) {
        const ts = turn.templateSection!;
        const sec = templateSections?.find((s) => String(s._id) === ts.id);
        if (!sec) throw new Error("Section not found");
        let nextDescription = sec.description;
        let nextExamples = [...(sec.examples || [])];
        let nextFormat = sec.formatTemplate;
        let nextTagIds: Id<"ruleTags">[] = [...((sec.tagIds as Id<"ruleTags">[]) || [])];
        if (change.target === "sectionDescription") {
          if (typeof change.newValue !== "string")
            throw new Error("Missing new description");
          nextDescription = change.newValue;
        } else if (change.target === "sectionFormatTemplate") {
          if (typeof change.newValue !== "string")
            throw new Error("Missing new format template");
          nextFormat = change.newValue;
        } else if (change.target === "sectionTags") {
          if (!change.tagId) throw new Error("Missing tag id");
          const tid = String(change.tagId);
          const currentIds = nextTagIds.map((id) => String(id));
          if (change.action === "add") {
            if (currentIds.includes(tid))
              throw new Error("Tag already attached to section");
            nextTagIds.push(change.tagId as Id<"ruleTags">);
          } else if (change.action === "remove") {
            if (!currentIds.includes(tid))
              throw new Error("Tag is not attached to section");
            nextTagIds = nextTagIds.filter((id) => String(id) !== tid);
          } else {
            throw new Error("Unsupported action for sectionTags");
          }
        } else {
          if (change.action === "add") {
            if (typeof change.newValue !== "string")
              throw new Error("Missing example text");
            nextExamples.push(change.newValue);
          } else if (change.action === "remove") {
            if (typeof change.exampleIndex !== "number")
              throw new Error("Missing example index");
            const removeIdx = change.exampleIndex - 1;
            if (removeIdx < 0 || removeIdx >= nextExamples.length)
              throw new Error("Example index out of range");
            nextExamples = nextExamples.filter((_, i) => i !== removeIdx);
          } else {
            if (
              typeof change.exampleIndex !== "number" ||
              typeof change.newValue !== "string"
            )
              throw new Error("Missing example index or text");
            const idx = change.exampleIndex - 1;
            if (idx < 0 || idx >= nextExamples.length)
              throw new Error("Example index out of range");
            nextExamples[idx] = change.newValue;
          }
        }
        await updateSection({
          id: ts.id as Id<"templateSections">,
          userId,
          name: sec.name,
          description: nextDescription,
          examples: nextExamples,
          adhereToFormatting: sec.adhereToFormatting,
          formatTemplate: nextFormat,
          doubleSpaceOutput: sec.doubleSpaceOutput,
          allowAssessment: sec.allowAssessment,
          tagIds: nextTagIds,
          provenance,
        });
      } else if (change.target === "tagRule") {
        if (change.action === "add") {
          if (!change.tagId) throw new Error("Missing tag id for new rule");
          if (typeof change.newValue !== "string" || !change.newValue.trim())
            throw new Error("Missing new rule content");
          await createRule({
            userId,
            content: change.newValue,
            tagId: change.tagId as Id<"ruleTags">,
            provenance,
          });
        } else if (change.action === "remove") {
          if (!change.ruleId) throw new Error("Missing rule id");
          await removeRule({
            id: change.ruleId as Id<"globalRules">,
            userId,
          });
        } else {
          if (!change.ruleId) throw new Error("Missing rule id");
          if (typeof change.newValue !== "string")
            throw new Error("Missing new rule content");
          await updateRule({
            id: change.ruleId as Id<"globalRules">,
            userId,
            content: change.newValue,
            provenance,
          });
        }
      }
      updateTurnAndPersist(turnIdx, (t) => ({
        ...t,
        appliedClusterKeys: new Set([
          ...Array.from(t.appliedClusterKeys || []),
          clusterKey,
        ]),
      }));
      setConflictChooser(null);
      toast({
        title: "Change applied",
        description: `Suggestion from ${proposal.agentName}`,
      });
    } catch (err: any) {
      toast({
        title: "Could not apply change",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setApplyingKey(null);
    }
  }

  function dismissClusterChange(turnIdx: number, clusterKey: string) {
    updateTurnAndPersist(turnIdx, (t) => ({
      ...t,
      dismissedClusterKeys: new Set([
        ...Array.from(t.dismissedClusterKeys || []),
        clusterKey,
      ]),
    }));
  }

  function dismissChange(turnIdx: number, changeIdx: number) {
    updateTurnAndPersist(turnIdx, (t) => ({
      ...t,
      dismissedChangeIds: new Set([
        ...Array.from(t.dismissedChangeIds || []),
        String(changeIdx),
      ]),
    }));
  }

  function isChangeStageable(c: ProposedChange) {
    return (
      c.target === "sectionDescription" ||
      c.target === "sectionExamples" ||
      c.target === "sectionFormatTemplate" ||
      c.target === "tagRule" ||
      c.target === "sectionTags"
    );
  }

  function isChangeStaged(turn: Turn, changeIdx: number): boolean {
    if (!stagedRow) return false;
    return (stagedRow.edits || []).some(
      (e: any) =>
        e.sourceTurnId === turn.id && e.sourceChangeIdx === changeIdx,
    );
  }

  async function stageChange(turnIdx: number, changeIdx: number) {
    const turn = turns[turnIdx];
    const change = turn?.proposedChanges?.[changeIdx];
    if (!change || !isChangeStageable(change)) return;
    if (!activeTemplateSection) {
      toast({
        title: "Cannot stage",
        description:
          "No matching template section was found, so this edit cannot be staged.",
        variant: "destructive",
      });
      return;
    }
    const key = `stage-${turn.id}-${changeIdx}`;
    setStagingKey(key);
    try {
      await stageEditMut({
        userId,
        sessionId,
        templateSectionId: activeTemplateSection._id,
        sectionTitle,
        edit: {
          target: change.target as
            | "sectionDescription"
            | "sectionExamples"
            | "sectionFormatTemplate"
            | "tagRule"
            | "sectionTags",
          action: change.action,
          newValue: change.newValue,
          exampleIndex: change.exampleIndex,
          ruleId: change.ruleId,
          tagId: change.tagId,
          agentId: turn.agentId || undefined,
          agentName: turn.agentName || undefined,
          sourceTurnId: turn.id,
          sourceChangeIdx: changeIdx,
          rationale: change.rationale,
          summary: describeChange(change),
        },
      });
      toast({ title: "Staged for preview" });
    } catch (err: any) {
      toast({
        title: "Could not stage change",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setStagingKey(null);
    }
  }

  async function stagePanelChange(
    turnIdx: number,
    clusterKey: string,
    proposal: PanelProposal,
  ) {
    const turn = turns[turnIdx];
    if (!turn) return;
    const change = proposal.change;
    if (!isChangeStageable(change)) return;
    if (!activeTemplateSection) {
      toast({
        title: "Cannot stage",
        description: "No matching template section was found.",
        variant: "destructive",
      });
      return;
    }
    const key = `stage-panel-${turn.id}-${clusterKey}-${proposal.agentId}`;
    setStagingKey(key);
    try {
      await stageEditMut({
        userId,
        sessionId,
        templateSectionId: activeTemplateSection._id,
        sectionTitle,
        edit: {
          target: change.target as
            | "sectionDescription"
            | "sectionExamples"
            | "sectionFormatTemplate"
            | "tagRule"
            | "sectionTags",
          action: change.action,
          newValue: change.newValue,
          exampleIndex: change.exampleIndex,
          ruleId: change.ruleId,
          tagId: change.tagId,
          agentId: proposal.agentId,
          agentName: proposal.agentName,
          sourceTurnId: turn.id,
          sourceChangeIdx: undefined,
          rationale: change.rationale,
          summary: describeChange(change),
        },
      });
      toast({ title: "Staged for preview" });
    } catch (err: any) {
      toast({
        title: "Could not stage change",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setStagingKey(null);
    }
  }

  function detectStaleEdits(): Map<string, string> {
    const errors = new Map<string, string>();
    if (!stagedRow || !activeTemplateSection) return errors;
    const sec = activeTemplateSection;
    const examples = sec.examples || [];
    const ruleIds = new Set(
      (globalRulesList || []).map((r: any) => String(r._id)),
    );
    for (const e of stagedRow.edits || []) {
      if (e.target === "sectionExamples") {
        if (e.action === "replace" || e.action === "remove") {
          const idx = typeof e.exampleIndex === "number" ? e.exampleIndex - 1 : -1;
          if (idx < 0 || idx >= examples.length) {
            errors.set(
              e.editId,
              `Example ${e.exampleIndex} no longer exists — remove or re-stage this edit.`,
            );
          }
        }
      } else if (e.target === "tagRule") {
        if (e.action === "add") {
          const sectionTagIds = new Set(
            (sec.tagIds || []).map((id: any) => String(id)),
          );
          if (!e.tagId || !sectionTagIds.has(String(e.tagId))) {
            errors.set(
              e.editId,
              "The target tag is no longer attached to this section — remove this edit.",
            );
          } else if (typeof e.newValue !== "string" || !e.newValue.trim()) {
            errors.set(
              e.editId,
              "The new rule has no content — remove this edit.",
            );
          }
        } else if (e.ruleId && !ruleIds.has(String(e.ruleId))) {
          errors.set(
            e.editId,
            "The target rule no longer exists — remove this edit.",
          );
        }
      } else if (e.target === "sectionTags") {
        const projected = new Set(
          (sec.tagIds || []).map((id: any) => String(id)),
        );
        const ordered = [...(stagedRow.edits || [])].sort(
          (a: any, b: any) => a.createdAt - b.createdAt,
        );
        for (const prior of ordered) {
          if (prior.editId === e.editId) break;
          if (prior.target !== "sectionTags") continue;
          if (errors.has(prior.editId)) continue;
          if (!prior.tagId) continue;
          if (prior.action === "add") projected.add(String(prior.tagId));
          else if (prior.action === "remove")
            projected.delete(String(prior.tagId));
        }
        if (!e.tagId) {
          errors.set(e.editId, "Missing tag id — remove this edit.");
        } else if (e.action === "add" && projected.has(String(e.tagId))) {
          errors.set(
            e.editId,
            "That tag is already attached to this section — remove this edit.",
          );
        } else if (e.action === "remove" && !projected.has(String(e.tagId))) {
          errors.set(
            e.editId,
            "That tag is no longer attached to this section — remove this edit.",
          );
        }
      }
    }
    return errors;
  }

  function buildOverridesFromStaged(): {
    overrides: any;
    skippedEditIds: Set<string>;
  } {
    const overrides: any = {
      tagRuleOverrides: [] as { ruleId: string; content: string }[],
      removedRuleIds: [] as string[],
      addedRules: [] as { tagId: string; content: string }[],
    };
    const skipped = new Set<string>();
    if (!stagedRow || !activeTemplateSection) {
      return { overrides, skippedEditIds: skipped };
    }
    const stale = detectStaleEdits();
    let nextDescription: string | undefined;
    let nextFormatTemplate: string | undefined;
    let nextExamples: string[] | undefined;
    let exampleOpsApplied = false;
    let nextTagIds: string[] | undefined;
    let tagOpsApplied = false;

    const sortedEdits = [...(stagedRow.edits || [])].sort(
      (a: any, b: any) => a.createdAt - b.createdAt,
    );
    for (const e of sortedEdits) {
      if (stale.has(e.editId)) {
        skipped.add(e.editId);
        continue;
      }
      if (e.target === "sectionDescription" && typeof e.newValue === "string") {
        nextDescription = e.newValue;
      } else if (
        e.target === "sectionFormatTemplate" &&
        typeof e.newValue === "string"
      ) {
        nextFormatTemplate = e.newValue;
      } else if (e.target === "sectionExamples") {
        if (nextExamples === undefined) {
          nextExamples = [...(activeTemplateSection.examples || [])];
        }
        if (e.action === "add" && typeof e.newValue === "string") {
          nextExamples.push(e.newValue);
          exampleOpsApplied = true;
        } else if (e.action === "remove" && typeof e.exampleIndex === "number") {
          const idx = e.exampleIndex - 1;
          if (idx >= 0 && idx < nextExamples.length) {
            nextExamples = nextExamples.filter((_, i) => i !== idx);
            exampleOpsApplied = true;
          }
        } else if (
          e.action === "replace" &&
          typeof e.exampleIndex === "number" &&
          typeof e.newValue === "string"
        ) {
          const idx = e.exampleIndex - 1;
          if (idx >= 0 && idx < nextExamples.length) {
            nextExamples[idx] = e.newValue;
            exampleOpsApplied = true;
          }
        }
      } else if (e.target === "tagRule") {
        if (
          e.action === "add" &&
          e.tagId &&
          typeof e.newValue === "string" &&
          e.newValue.trim()
        ) {
          overrides.addedRules.push({
            tagId: String(e.tagId),
            content: e.newValue,
          });
        } else if (e.ruleId) {
          if (e.action === "remove") {
            overrides.removedRuleIds.push(String(e.ruleId));
          } else if (typeof e.newValue === "string") {
            overrides.tagRuleOverrides.push({
              ruleId: String(e.ruleId),
              content: e.newValue,
            });
          }
        }
      } else if (e.target === "sectionTags") {
        if (nextTagIds === undefined) {
          nextTagIds = (activeTemplateSection.tagIds || []).map((id: any) =>
            String(id),
          );
        }
        if (e.tagId) {
          const tid = String(e.tagId);
          if (e.action === "add" && !nextTagIds.includes(tid)) {
            nextTagIds.push(tid);
            tagOpsApplied = true;
          } else if (e.action === "remove" && nextTagIds.includes(tid)) {
            nextTagIds = nextTagIds.filter((id) => id !== tid);
            tagOpsApplied = true;
          }
        }
      }
    }
    if (nextDescription !== undefined) overrides.sectionDescription = nextDescription;
    if (nextFormatTemplate !== undefined)
      overrides.sectionFormatTemplate = nextFormatTemplate;
    if (exampleOpsApplied && nextExamples) overrides.sectionExamples = nextExamples;
    if (tagOpsApplied && nextTagIds) overrides.tagIds = nextTagIds;
    return { overrides, skippedEditIds: skipped };
  }

  async function previewRegenerate() {
    if (!activeTemplateSection || !stagedRow || stagedRow.edits.length === 0) return;
    if (!session) return;
    const stale = detectStaleEdits();
    const perItem: Record<string, string> = {};
    stale.forEach((msg, editId) => {
      perItem[editId] = msg;
    });
    const { overrides, skippedEditIds } = buildOverridesFromStaged();
    if (skippedEditIds.size === stagedRow.edits.length) {
      setStagedItemErrors(perItem);
      toast({
        title: "All staged edits are stale",
        description: "Remove the stale items and re-stage.",
        variant: "destructive",
      });
      return;
    }
    setIsPreviewing(true);
    setPreviewError(null);
    try {
      const sec = activeTemplateSection;
      const payload = {
        transcription: session.transcription || transcription || "",
        notes: (session as any).notes || "",
        templateSectionId: sec._id,
        section: {
          name: sec.name,
          description: sec.description,
          examples: sec.examples,
          adhereToFormatting: sec.adhereToFormatting,
          formatTemplate: sec.formatTemplate,
          doubleSpaceOutput: sec.doubleSpaceOutput,
          allowAssessment: sec.allowAssessment,
          tagIds: sec.tagIds,
        },
        patientName: session.patientName || "",
        gender: session.gender || undefined,
        sessionTitle: session.title || "",
        overrides,
      };
      const resp = await apiRequest(
        "POST",
        "/api/regenerate-section/preview",
        payload,
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || "Preview failed");
      const content: string = data.content || "";
      await setPreviewMut({
        userId,
        sessionId,
        templateSectionId: sec._id,
        previewContent: content,
      });
      setStagedItemErrors(perItem);
    } catch (err: any) {
      setPreviewError(err?.message || "Preview failed. Please try again.");
    } finally {
      setIsPreviewing(false);
    }
  }

  async function applyStaged() {
    if (!activeTemplateSection || !stagedRow || stagedRow.edits.length === 0) return;
    if (!stagedRow.previewContent) {
      toast({
        title: "Preview required",
        description: "Run Preview regenerate before applying staged edits.",
      });
      return;
    }
    setIsApplyingStaged(true);
    const sec = activeTemplateSection;
    try {
      const result = await applyStagedEdits({
        row: stagedRow,
        section: sec,
        rules: globalRulesList ?? [],
        userId,
        sessionId,
        mutations: {
          updateSection,
          updateRule,
          removeRule,
          createRule,
          applySectionContentEdit: applyEditDoc,
          removeStagedEdit: removeStagedEditMut,
          clearStaged: clearStagedMut,
          clearPreview: clearPreviewMut,
        },
      });

      // Mark originating turn changes as applied for any appliedEdits.
      const appliedIds = new Set(result.appliedEditIds);
      const appliedByTurn = new Map<string, Set<string>>();
      for (const e of stagedRow.edits) {
        if (!appliedIds.has(e.editId)) continue;
        if (e.sourceTurnId && typeof e.sourceChangeIdx === "number") {
          if (!appliedByTurn.has(e.sourceTurnId))
            appliedByTurn.set(e.sourceTurnId, new Set());
          appliedByTurn.get(e.sourceTurnId)!.add(String(e.sourceChangeIdx));
        }
      }
      appliedByTurn.forEach((ids, turnId) => {
        const ti = turns.findIndex((t) => t.id === turnId);
        if (ti >= 0) {
          updateTurnAndPersist(ti, (t) => ({
            ...t,
            appliedChangeIds: new Set([
              ...Array.from(t.appliedChangeIds || []),
              ...Array.from(ids),
            ]),
          }));
        }
      });

      setStagedItemErrors(result.errors);
      const numApplied = result.appliedEditIds.length;
      const numErrors = Object.keys(result.errors).length;
      if (numErrors === 0) {
        toast({
          title: "Staged changes applied",
          description: `${numApplied} edit${numApplied === 1 ? "" : "s"} committed.`,
        });
      } else {
        toast({
          title:
            numApplied > 0
              ? "Some staged edits failed"
              : "Could not apply staged edits",
          description:
            "Review the errors on the remaining items, then try again.",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({
        title: "Could not apply staged changes",
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setIsApplyingStaged(false);
    }
  }

  async function discardStaged() {
    if (!activeTemplateSection) return;
    try {
      await clearStagedMut({
        userId,
        sessionId,
        templateSectionId: activeTemplateSection._id,
      });
      setStagedItemErrors({});
      toast({ title: "Staged changes discarded" });
    } catch (err: any) {
      toast({
        title: "Could not discard",
        description: err?.message,
        variant: "destructive",
      });
    }
  }

  async function removeStagedEdit(editId: string) {
    if (!activeTemplateSection) return;
    try {
      await removeStagedEditMut({
        userId,
        sessionId,
        templateSectionId: activeTemplateSection._id,
        editId,
      });
      setStagedItemErrors((prev) => {
        const next = { ...prev };
        delete next[editId];
        return next;
      });
    } catch (err: any) {
      toast({
        title: "Could not remove",
        description: err?.message,
        variant: "destructive",
      });
    }
  }

  const userAgentOptions = useMemo(
    () =>
      (agents || []).map((a) => ({
        id: String(a._id),
        name: a.name,
        description: a.description,
      })),
    [agents],
  );

  const hasSectionHighlights = sectionHighlights.length > 0;
  const hasTranscriptHighlights = transcriptHighlights.length > 0;
  const hasAnyHighlights = hasSectionHighlights || hasTranscriptHighlights;

  return (
    <>
    <div
      className="h-full flex flex-col bg-background"
      data-testid="section-assistant-pane"
    >
        {!hideHeader && (
        <div className="p-4 pb-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <Sparkles className="w-4 h-4 text-primary" />
            Section Assistant — {sectionTitle}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Ask about the “{sectionTitle}” section. The assistant can read the
            transcript, notes, section content, description, examples,
            formatting, and tag-scoped rules, and can propose edits for you to
            apply.
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Bot className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Agent:</span>
            <Popover open={agentPickerOpen} onOpenChange={setAgentPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs justify-between gap-2 min-w-[16rem]"
                  data-testid="button-section-assistant-agent-picker"
                >
                  <span className="truncate text-left">
                    {selectedAgentIds.length === 0
                      ? "Auto-pick best agent"
                      : selectedAgentIds.length === 1
                        ? userAgentOptions.find(
                            (a) => a.id === selectedAgentIds[0],
                          )?.name || "1 agent"
                        : `Panel: ${selectedAgentIds.length} agents`}
                  </span>
                  <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="start">
                <div className="text-xs text-muted-foreground px-2 py-1">
                  Select up to 3 agents. Pick 2 or more to run a panel review.
                </div>
                <div className="max-h-64 overflow-auto">
                  <button
                    type="button"
                    className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover-elevate text-sm"
                    onClick={() => setSelectedAgentIds([])}
                    data-testid="button-agent-auto"
                  >
                    <Checkbox
                      checked={selectedAgentIds.length === 0}
                      className="pointer-events-none"
                    />
                    <span>Auto-pick best agent</span>
                  </button>
                  {userAgentOptions.map((a) => {
                    const checked = selectedAgentIds.includes(a.id);
                    const atMax = selectedAgentIds.length >= 3 && !checked;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover-elevate text-sm disabled:opacity-50"
                        disabled={atMax}
                        onClick={() => {
                          setSelectedAgentIds((prev) =>
                            prev.includes(a.id)
                              ? prev.filter((x) => x !== a.id)
                              : prev.length >= 3
                                ? prev
                                : [...prev, a.id],
                          );
                        }}
                        data-testid={`button-agent-toggle-${a.id}`}
                      >
                        <Checkbox
                          checked={checked}
                          className="pointer-events-none"
                        />
                        <span className="truncate">{a.name}</span>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            {selectedAgentIds.length >= 2 && (
              <Badge variant="secondary" className="text-[10px]">
                <Users className="w-3 h-3 mr-1" />
                Panel mode
              </Badge>
            )}
          </div>
        </div>
        )}

        {activeTurn && (
          <div
            className="border-b border-border shrink-0"
            data-testid="section-assistant-preview"
          >
            <button
              type="button"
              onClick={() => setPreviewOpen((o) => !o)}
              className="w-full flex items-center justify-between px-6 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/50"
              data-testid="button-toggle-preview"
            >
              <span className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                {hasAnyHighlights
                  ? `Section view — ${sectionHighlights.length} highlight${sectionHighlights.length === 1 ? "" : "s"}${
                      hasTranscriptHighlights
                        ? `, ${transcriptHighlights.length} in transcript`
                        : ""
                    }`
                  : "Section view (no specific spans to highlight)"}
              </span>
              {previewOpen ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
            </button>
            {previewOpen && (
              <div className="px-6 pb-3 space-y-3 max-h-56 overflow-auto">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                    Section content
                  </div>
                  <div
                    className="text-sm leading-relaxed bg-muted/30 rounded-md p-3 border border-border"
                    data-testid="section-preview-content"
                  >
                    <HighlightedText
                      text={sectionContent}
                      highlights={sectionHighlights}
                      comments={comments}
                      source="section"
                      hoveredCommentId={hoveredCommentId}
                      setHoveredCommentId={setHoveredCommentId}
                      onSpanClick={handleSpanClick}
                      testIdPrefix="highlight-section"
                    />
                  </div>
                </div>
                {hasTranscriptHighlights && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                      Transcript
                    </div>
                    <div
                      className="text-sm leading-relaxed bg-muted/30 rounded-md p-3 border border-border"
                      data-testid="section-preview-transcript"
                    >
                      <HighlightedText
                        text={transcription || ""}
                        highlights={transcriptHighlights}
                        comments={comments}
                        source="transcript"
                        hoveredCommentId={hoveredCommentId}
                        setHoveredCommentId={setHoveredCommentId}
                        onSpanClick={handleSpanClick}
                        testIdPrefix="highlight-transcript"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <ScrollArea className="flex-1 min-h-0">
          <div ref={scrollRef} className="p-6 pt-14 lg:pt-6 space-y-4">
            {turns.length === 0 && askingPreset === null && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Quick actions:</p>
                <div className="grid gap-2 sm:grid-cols-1">
                  <Button
                    variant="outline"
                    className="justify-start h-auto py-3"
                    onClick={() => send("conflicts")}
                    disabled={isSending}
                    data-testid="button-preset-conflicts"
                  >
                    <ShieldCheck className="w-4 h-4 mr-2 text-primary shrink-0" />
                    <div className="text-left">
                      <div className="text-sm font-medium">
                        Check the prompts for conflicts
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Looks for contradictions between description, examples,
                        formatting, and tag rules.
                      </div>
                    </div>
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start h-auto py-3"
                    onClick={() => setAskingPreset("why-sentence")}
                    disabled={isSending}
                    data-testid="button-preset-why-sentence"
                  >
                    <HelpCircle className="w-4 h-4 mr-2 text-primary shrink-0" />
                    <div className="text-left">
                      <div className="text-sm font-medium">
                        Why was a sentence included?
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Trace a specific sentence back to the transcript, notes,
                        or prompts.
                      </div>
                    </div>
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start h-auto py-3"
                    onClick={() => send("simplify")}
                    disabled={isSending}
                    data-testid="button-preset-simplify"
                  >
                    <Scissors className="w-4 h-4 mr-2 text-primary shrink-0" />
                    <div className="text-left">
                      <div className="text-sm font-medium">
                        Simplify or consolidate prompts
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Review the description, examples, formatting, and tag
                        rules for redundancy.
                      </div>
                    </div>
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground pt-2">
                  Or type your own question below.
                </div>
              </div>
            )}

            {askingPreset === "why-sentence" && turns.length === 0 && (
              <div className="border border-border rounded-md p-3 space-y-2">
                <div className="text-sm font-medium">
                  Paste the sentence (optional)
                </div>
                <Textarea
                  value={presetParamSentence}
                  onChange={(e) => setPresetParamSentence(e.target.value)}
                  placeholder="Paste a sentence from the generated section, or leave blank to walk through all of them."
                  rows={3}
                  data-testid="textarea-preset-sentence"
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAskingPreset(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() =>
                      send(
                        "why-sentence",
                        presetParamSentence.trim() || undefined,
                      )
                    }
                    disabled={isSending}
                    data-testid="button-preset-sentence-submit"
                  >
                    Ask
                  </Button>
                </div>
              </div>
            )}

            {turns.map((t, ti) => {
              const isActive = activeTurnId === t.id;
              return (
                <div
                  key={t.id}
                  className={
                    t.role === "user"
                      ? "flex items-end gap-1 justify-end"
                      : "flex justify-start"
                  }
                  data-testid={`section-assistant-turn-${t.role}-${ti}`}
                >
                  {t.role === "user" && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-foreground"
                            onClick={() => void send(undefined, undefined, t.content)}
                            disabled={isSending}
                            aria-label="Resend message"
                            data-testid={`button-resend-message-${t.id}`}
                          >
                            <RefreshCcw className="w-3.5 h-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="left">Resend message</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <div
                    className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
                      t.role === "user"
                        ? "bg-primary text-primary-foreground whitespace-pre-wrap"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    {t.role === "assistant" && t.agentName && !t.panel && (
                      <div className="flex items-center gap-1 mb-1">
                        <Badge variant="secondary" className="text-[10px]">
                          <Bot className="w-3 h-3 mr-1" />
                          {t.agentName}
                        </Badge>
                      </div>
                    )}
                    {t.role === "assistant" && t.panel && (
                      <PanelTurnView
                        turn={t}
                        turnIdx={ti}
                        applyingKey={applyingKey}
                        stagingKey={stagingKey}
                        conflictChooser={conflictChooser}
                        setConflictChooser={setConflictChooser}
                        onApply={applyPanelChange}
                        onPreview={requestPanelPreview}
                        onDismiss={dismissClusterChange}
                        onStage={stagePanelChange}
                        isProposalStaged={(turn, clusterKey, p) => {
                          if (!stagedRow) return false;
                          return (stagedRow.edits || []).some(
                            (e: any) =>
                              e.sourceTurnId === turn.id &&
                              e.target === p.change.target &&
                              e.action === p.change.action &&
                              (e.ruleId || undefined) ===
                                ((p.change as any).ruleId || undefined) &&
                              (e.tagId || undefined) ===
                                ((p.change as any).tagId || undefined) &&
                              (e.exampleIndex ?? null) ===
                                ((p.change as any).exampleIndex ?? null) &&
                              (typeof e.newValue === "string"
                                ? e.newValue
                                : null) ===
                                (typeof (p.change as any).newValue === "string"
                                  ? (p.change as any).newValue
                                  : null) &&
                              (e.agentId || undefined) === p.agentId,
                          );
                        }}
                        isStageable={(p) => isChangeStageable(p.change)}
                      />
                    )}
                    {!(t.role === "assistant" && t.panel) && (<>
                    {t.role === "user" && t.focusQuote && (
                      <div
                        className="mb-1.5 rounded border border-primary-foreground/30 bg-primary-foreground/10 px-2 py-1 text-[11px] italic"
                        data-testid={`text-focus-quote-${ti}`}
                      >
                        <div className="opacity-80 not-italic font-medium mb-0.5">
                          About this quote:
                        </div>
                        <div className="line-clamp-3">“{t.focusQuote}”</div>
                      </div>
                    )}
                    <div
                      ref={(el) => {
                        if (isActive && el) {
                          commentRefs.current.set("answer", el);
                        }
                      }}
                      onMouseEnter={
                        isActive
                          ? () => setHoveredCommentId("answer")
                          : undefined
                      }
                      onMouseLeave={
                        isActive ? () => setHoveredCommentId(null) : undefined
                      }
                      className={`rounded transition-shadow ${
                        isActive && hoveredCommentId === "answer"
                          ? "ring-2 ring-yellow-500"
                          : ""
                      }`}
                      data-testid={`comment-answer-${ti}`}
                    >
                      {t.role === "assistant" ? (
                        <MarkdownMessage content={t.content} />
                      ) : (
                        t.content
                      )}
                      {isActive &&
                        t.spans &&
                        t.spans.length > 0 &&
                        sectionHighlights.length + transcriptHighlights.length >
                          0 && (
                          <div className="mt-1">
                            <Badge
                              variant="outline"
                              className="text-[10px] gap-1"
                            >
                              <Sparkles className="w-2.5 h-2.5" />
                              {sectionHighlights.filter(
                                (h) => h.commentId === "answer",
                              ).length +
                                transcriptHighlights.filter(
                                  (h) => h.commentId === "answer",
                                ).length}{" "}
                              highlighted
                            </Badge>
                          </div>
                        )}
                    </div>
                    {t.role === "assistant" &&
                      t.proposedChanges &&
                      t.proposedChanges.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <div className="text-xs font-semibold text-muted-foreground">
                            Proposed changes
                          </div>
                          {t.proposedChanges.map((c, ci) => {
                            const k = changeKey(t, ci);
                            const applied = t.appliedChangeIds?.has(String(ci));
                            const dismissed = t.dismissedChangeIds?.has(
                              String(ci),
                            );
                            if (dismissed) return null;
                            const commentId = `change-${ci}`;
                            const isCommentHovered =
                              isActive && hoveredCommentId === commentId;
                            const changeHighlightCount = isActive
                              ? sectionHighlights.filter(
                                  (h) => h.commentId === commentId,
                                ).length +
                                transcriptHighlights.filter(
                                  (h) => h.commentId === commentId,
                                ).length
                              : 0;
                            return (
                              <div
                                key={ci}
                                ref={(el) => {
                                  if (isActive && el)
                                    commentRefs.current.set(commentId, el);
                                }}
                                onMouseEnter={
                                  isActive
                                    ? () => setHoveredCommentId(commentId)
                                    : undefined
                                }
                                onMouseLeave={
                                  isActive
                                    ? () => setHoveredCommentId(null)
                                    : undefined
                                }
                                className={`border border-border rounded-md p-3 bg-background transition-shadow ${
                                  isCommentHovered
                                    ? "ring-2 ring-yellow-500"
                                    : ""
                                }`}
                                data-testid={`proposed-change-${ti}-${ci}`}
                              >
                                <div className="text-xs font-medium text-foreground flex items-center justify-between gap-2">
                                  <span>
                                    {c.target === "tagRule" && c.action === "add"
                                      ? `Add a new global rule for tag: ${
                                          t.sectionTags?.find(
                                            (s) => s.id === c.tagId,
                                          )?.name || "(unknown)"
                                        }`
                                      : c.target === "sectionTags"
                                      ? (() => {
                                          const tagName =
                                            t.availableTags?.find(
                                              (s) => s.id === c.tagId,
                                            )?.name ||
                                            t.sectionTags?.find(
                                              (s) => s.id === c.tagId,
                                            )?.name ||
                                            c.tagId ||
                                            "(unknown tag)";
                                          if (c.action === "add")
                                            return `Attach tag to this section: ${tagName}`;
                                          if (c.action === "remove")
                                            return `Remove tag from this section: ${tagName}`;
                                          return describeChange(c);
                                        })()
                                      : describeChange(c)}
                                  </span>
                                  {changeHighlightCount > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] gap-1 shrink-0"
                                    >
                                      <Sparkles className="w-2.5 h-2.5" />
                                      {changeHighlightCount}
                                    </Badge>
                                  )}
                                </div>
                                {c.rationale && (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {c.rationale}
                                  </div>
                                )}
                                {"newValue" in c &&
                                  typeof (c as any).newValue === "string" && (
                                    <pre className="mt-2 text-xs bg-muted/60 rounded p-2 whitespace-pre-wrap font-mono max-h-40 overflow-auto">
                                      {(c as any).newValue}
                                    </pre>
                                  )}
                                <div className="flex items-center gap-2 mt-2">
                                  {applied ? (
                                    <Badge
                                      variant="default"
                                      className="text-[10px]"
                                    >
                                      <Check className="w-3 h-3 mr-1" />
                                      Applied
                                    </Badge>
                                  ) : (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="default"
                                        onClick={() => requestApply(ti, ci)}
                                        disabled={applyingKey === k}
                                        data-testid={`button-apply-change-${ti}-${ci}`}
                                      >
                                        {applyingKey === k ? (
                                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                        ) : (
                                          <Check className="w-3 h-3 mr-1" />
                                        )}
                                        Apply
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => requestPreview(ti, ci)}
                                        disabled={applyingKey === k}
                                        data-testid={`button-view-diff-${ti}-${ci}`}
                                      >
                                        <Eye className="w-3 h-3 mr-1" />
                                        View Diff
                                      </Button>
                                      {isChangeStageable(c) && (
                                        isChangeStaged(t, ci) ? (
                                          <Badge
                                            variant="outline"
                                            className="text-[10px]"
                                            data-testid={`badge-staged-${ti}-${ci}`}
                                          >
                                            <Layers className="w-3 h-3 mr-1" />
                                            Staged
                                          </Badge>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="secondary"
                                            onClick={() => void stageChange(ti, ci)}
                                            disabled={
                                              stagingKey ===
                                                `stage-${t.id}-${ci}` ||
                                              applyingKey === k
                                            }
                                            data-testid={`button-stage-change-${ci}`}
                                          >
                                            {stagingKey ===
                                            `stage-${t.id}-${ci}` ? (
                                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                            ) : (
                                              <Layers className="w-3 h-3 mr-1" />
                                            )}
                                            Stage
                                          </Button>
                                        )
                                      )}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => dismissChange(ti, ci)}
                                        disabled={applyingKey === k}
                                        data-testid={`button-dismiss-change-${ti}-${ci}`}
                                      >
                                        <X className="w-3 h-3 mr-1" />
                                        Dismiss
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>)}
                  </div>
                </div>
              );
            })}

            {isSending && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-3 py-2 text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Thinking…
                </div>
              </div>
            )}

            {stagedRow && stagedRow.edits.length > 0 && (isPreviewing || previewError || stagedRow.previewContent) && (
              <div
                className="flex justify-start"
                data-testid="section-assistant-preview-entry"
              >
                <div className="max-w-[90%] w-full rounded-lg border border-amber-300/70 dark:border-amber-700/60 bg-amber-50/60 dark:bg-amber-950/30 px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <Badge variant="secondary" className="text-[10px] gap-1">
                      <Eye className="w-3 h-3" />
                      Preview
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {stagedRow.edits.length} staged edit
                      {stagedRow.edits.length === 1 ? "" : "s"}
                    </Badge>
                    {stagedRow.previewGeneratedAt && !isPreviewing && !previewError && (
                      <span className="text-[11px] text-muted-foreground">
                        Regenerated{" "}
                        {new Date(stagedRow.previewGeneratedAt).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                  {isPreviewing ? (
                    <div
                      className="flex items-center gap-2 text-muted-foreground italic py-1"
                      data-testid="text-preview-generating"
                    >
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating preview…
                    </div>
                  ) : previewError ? (
                    <div
                      className="flex items-start gap-2 text-destructive"
                      data-testid="text-preview-error"
                    >
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium">Could not generate preview</div>
                        <div className="text-xs opacity-90 mt-0.5">{previewError}</div>
                      </div>
                    </div>
                  ) : stagedRow.previewContent ? (
                    (() => {
                      const raw = stagedRow.previewContent as string;
                      const m = raw.match(
                        /^\s*<!--SECTION_ERROR:(system|missing|conflict):([\s\S]*?)-->\s*/,
                      );
                      if (m) {
                        const msg = m[2].replace(/--&gt;/g, "-->").trim();
                        return (
                          <div
                            className="flex items-start gap-2 text-destructive"
                            data-testid="text-preview-error"
                          >
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <div>
                              <div className="font-medium">
                                {m[1] === "conflict"
                                  ? "Conflicting instructions"
                                  : m[1] === "missing"
                                    ? "Not enough source information"
                                    : "Preview problem"}
                              </div>
                              <div className="text-xs opacity-90 mt-0.5">{msg}</div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div
                          className="rounded-md bg-background/70 border border-border p-3"
                          data-testid="text-preview-content"
                        >
                          <MarkdownMessage content={raw} />
                        </div>
                      );
                    })()
                  ) : null}
                  {!isPreviewing && (
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void previewRegenerate()}
                        disabled={isPreviewing || stagedRow.edits.length === 0}
                        data-testid="button-preview-regenerate-inline"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        {previewError || !stagedRow.previewContent
                          ? "Try again"
                          : "Regenerate preview"}
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => void applyStaged()}
                        disabled={
                          isApplyingStaged ||
                          !stagedRow.previewContent ||
                          stagedRow.edits.length === 0
                        }
                        title={
                          !stagedRow.previewContent
                            ? "Run Preview regenerate first so you can evaluate the result before applying."
                            : undefined
                        }
                        data-testid="button-apply-staged-inline"
                      >
                        {isApplyingStaged ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Check className="w-3 h-3 mr-1" />
                        )}
                        Apply staged
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void discardStaged()}
                        disabled={isApplyingStaged}
                        data-testid="button-discard-staged-inline"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Discard
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {stagedRow && stagedRow.edits.length > 0 && (
          <div
            className="border-t border-border bg-amber-50/40 dark:bg-amber-950/20 shrink-0"
            data-testid="section-assistant-staged-tray"
          >
            <div className="px-4 py-2 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setTrayCollapsed((v) => !v)}
                className="flex items-center gap-2 text-sm font-medium hover:opacity-80"
                data-testid="button-toggle-staged-tray"
              >
                <Layers3 className="w-4 h-4 text-amber-600" />
                <span>Staged changes</span>
                <Badge variant="secondary" className="text-[10px]">
                  {stagedRow.edits.length}
                </Badge>
                {stagedRow.previewGeneratedAt ? (
                  <span className="text-[11px] text-muted-foreground">
                    Preview {new Date(stagedRow.previewGeneratedAt).toLocaleTimeString()}
                  </span>
                ) : (
                  <span className="text-[11px] text-muted-foreground">
                    No preview yet
                  </span>
                )}
                {trayCollapsed ? (
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                ) : (
                  <ChevronUp className="w-3.5 h-3.5 opacity-60" />
                )}
              </button>
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void previewRegenerate()}
                  disabled={isPreviewing || stagedRow.edits.length === 0}
                  data-testid="button-preview-regenerate"
                >
                  {isPreviewing ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Eye className="w-3 h-3 mr-1" />
                  )}
                  Preview regenerate
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => void applyStaged()}
                  disabled={
                    isApplyingStaged ||
                    !stagedRow.previewContent ||
                    stagedRow.edits.length === 0
                  }
                  title={
                    !stagedRow.previewContent
                      ? "Run Preview regenerate first so you can evaluate the result before applying."
                      : undefined
                  }
                  data-testid="button-apply-staged"
                >
                  {isApplyingStaged ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Check className="w-3 h-3 mr-1" />
                  )}
                  Apply staged
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void discardStaged()}
                  disabled={isApplyingStaged}
                  data-testid="button-discard-staged"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Discard
                </Button>
              </div>
            </div>
            {!trayCollapsed && (
              <div className="px-4 pb-3 space-y-2 max-h-56 overflow-auto">
                {stagedRow.edits.map((e: any) => {
                  const err = stagedItemErrors[e.editId];
                  return (
                    <div
                      key={e.editId}
                      className={`rounded-md border p-2 text-xs bg-background ${err ? "border-destructive/60" : "border-border"}`}
                      data-testid={`staged-item-${e.editId}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">
                            {e.summary ||
                              describeChange({
                                target: e.target,
                                action: e.action,
                                exampleIndex: e.exampleIndex,
                              } as ProposedChange)}
                          </div>
                          {e.rationale && (
                            <div className="text-muted-foreground mt-0.5 line-clamp-2">
                              {e.rationale}
                            </div>
                          )}
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <Badge variant="outline" className="text-[10px]">
                              {e.target}
                            </Badge>
                            {e.agentName && (
                              <Badge variant="secondary" className="text-[10px]">
                                <Bot className="w-2.5 h-2.5 mr-1" />
                                {e.agentName}
                              </Badge>
                            )}
                          </div>
                          {err && (
                            <div
                              className="mt-1 text-destructive flex items-center gap-1"
                              data-testid={`staged-item-error-${e.editId}`}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {err}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void removeStagedEdit(e.editId)}
                          data-testid={`button-remove-staged-${e.editId}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="p-4 border-t border-border shrink-0">
          {scope && (
            <div
              className="mb-2 flex items-start gap-2 rounded border border-primary/40 bg-primary/5 px-2 py-1.5 text-xs"
              data-testid="badge-section-assistant-scope"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-primary mb-0.5">
                  Asking about this quote
                </div>
                <div className="italic text-foreground/80 line-clamp-3">
                  “{scope}”
                </div>
              </div>
              <button
                type="button"
                onClick={() => setScope(null)}
                className="p-0.5 rounded hover:bg-muted shrink-0"
                title="Clear scope"
                data-testid="button-clear-scope"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="relative">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={
                scope
                  ? "Ask about the highlighted quote..."
                  : "Ask about this section..."
              }
              rows={2}
              className="resize-none pr-12"
              data-testid="textarea-section-assistant-input"
            />
            <Button
              size="icon"
              className="absolute right-2 bottom-2 h-8 w-8"
              onClick={() => send()}
              disabled={!input.trim() || isSending}
              data-testid="button-section-assistant-send"
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
      {pendingApply && (
        <DiffPreviewModal
          open={!!pendingApply}
          onOpenChange={(o) => {
            if (!o) setPendingApply(null);
          }}
          label={pendingApply.label}
          oldValue={pendingApply.oldValue}
          newValue={pendingApply.newValue}
          intent={pendingApply.intent}
          isApplying={
            pendingApply.kind === "single"
              ? applyingKey ===
                changeKey(
                  turns[pendingApply.turnIdx],
                  pendingApply.changeIdx,
                )
              : applyingKey ===
                `panel-${turns[pendingApply.turnIdx]?.id}-${pendingApply.clusterKey}-${pendingApply.proposal.agentId}`
          }
          onCancel={() => setPendingApply(null)}
          onConfirm={async () => {
            const current = pendingApply;
            if (current.kind === "single") {
              await executeApply(current.turnIdx, current.changeIdx);
            } else {
              await applyPanelChange(
                current.turnIdx,
                current.clusterKey,
                current.proposal,
              );
            }
            setPendingApply(null);
          }}
        />
      )}
    </>
  );
}

function PanelTurnView({
  turn,
  turnIdx,
  applyingKey,
  stagingKey,
  conflictChooser,
  setConflictChooser,
  onApply,
  onPreview,
  onDismiss,
  onStage,
  isProposalStaged,
  isStageable,
}: {
  turn: Turn;
  turnIdx: number;
  applyingKey: string | null;
  stagingKey: string | null;
  conflictChooser: { turnIdx: number; clusterKey: string } | null;
  setConflictChooser: (
    v: { turnIdx: number; clusterKey: string } | null,
  ) => void;
  onApply: (
    turnIdx: number,
    clusterKey: string,
    proposal: PanelProposal,
  ) => void;
  onPreview: (
    turnIdx: number,
    clusterKey: string,
    proposal: PanelProposal,
  ) => void;
  onDismiss: (turnIdx: number, clusterKey: string) => void;
  onStage: (
    turnIdx: number,
    clusterKey: string,
    proposal: PanelProposal,
  ) => void;
  isProposalStaged: (
    turn: Turn,
    clusterKey: string,
    proposal: PanelProposal,
  ) => boolean;
  isStageable: (proposal: PanelProposal) => boolean;
}) {
  const panel = turn.panel!;
  const N = panel.agents.length;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1">
        <Badge variant="secondary" className="text-[10px]">
          <Users className="w-3 h-3 mr-1" />
          Panel of {N} agents
        </Badge>
      </div>
      <div
        className={`grid gap-2 ${N === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-3"}`}
      >
        {panel.perAgent.map((a) => (
          <div
            key={a.agentId}
            className="border border-border rounded-md p-2 bg-background"
            data-testid={`panel-agent-answer-${a.agentId}`}
          >
            <div className="flex items-center gap-1 mb-1">
              <Bot className="w-3 h-3 text-primary" />
              <span className="text-xs font-medium truncate">
                {a.agentName}
              </span>
            </div>
            <div className="text-xs text-muted-foreground whitespace-pre-wrap max-h-40 overflow-auto">
              {a.error ? `Error: ${a.error}` : a.answer || "(no answer)"}
            </div>
          </div>
        ))}
      </div>

      {panel.clusters.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-muted-foreground">
            Issues raised
          </div>
          {panel.clusters.map((cl) => {
            const applied = turn.appliedClusterKeys?.has(cl.key);
            const dismissed = turn.dismissedClusterKeys?.has(cl.key);
            if (dismissed) return null;
            const choosing =
              conflictChooser &&
              conflictChooser.turnIdx === turnIdx &&
              conflictChooser.clusterKey === cl.key;
            return (
              <div
                key={cl.key}
                className="border border-border rounded-md p-3 bg-background"
                data-testid={`panel-cluster-${turnIdx}-${cl.key}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-xs font-medium text-foreground">
                    {cl.label}
                  </div>
                  {cl.consensus && N > 1 && (
                    <Badge variant="default" className="text-[10px]">
                      <Check className="w-3 h-3 mr-1" />
                      Consensus ({cl.proposals.length}/{N} agents)
                    </Badge>
                  )}
                  {cl.singleReviewer && N > 1 && (
                    <Badge variant="outline" className="text-[10px]">
                      Single reviewer
                    </Badge>
                  )}
                  {cl.conflict && (
                    <Badge variant="destructive" className="text-[10px]">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Disagreement
                    </Badge>
                  )}
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-1">
                  {cl.proposals.map((p, pi) => {
                    const stageKey = `stage-panel-${turn.id}-${cl.key}-${p.agentId}`;
                    const staged = isProposalStaged(turn, cl.key, p);
                    return (
                    <div
                      key={`${p.agentId}-${pi}`}
                      className="border border-border/70 rounded p-2 bg-muted/40"
                      data-testid={`panel-proposal-${turnIdx}-${cl.key}-${p.agentId}`}
                    >
                      <div className="flex items-center gap-1 mb-1">
                        <Badge
                          variant="secondary"
                          className="text-[10px]"
                        >
                          <Bot className="w-3 h-3 mr-1" />
                          {p.agentName}
                        </Badge>
                        {isStageable(p) && !applied && (
                          staged ? (
                            <Badge
                              variant="outline"
                              className="text-[10px]"
                              data-testid={`badge-staged-panel-${turnIdx}-${cl.key}-${p.agentId}`}
                            >
                              <Layers className="w-3 h-3 mr-1" />
                              Staged
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2"
                              onClick={() => onStage(turnIdx, cl.key, p)}
                              disabled={stagingKey === stageKey}
                              data-testid={`button-stage-panel-${turnIdx}-${cl.key}-${p.agentId}`}
                            >
                              {stagingKey === stageKey ? (
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Layers className="w-3 h-3 mr-1" />
                              )}
                              Stage
                            </Button>
                          )
                        )}
                      </div>
                      {p.change.rationale && (
                        <div className="text-xs text-muted-foreground">
                          {p.change.rationale}
                        </div>
                      )}
                      {typeof p.change.newValue === "string" && (
                        <pre className="mt-1 text-xs bg-muted/60 rounded p-2 whitespace-pre-wrap font-mono max-h-32 overflow-auto">
                          {p.change.newValue}
                        </pre>
                      )}
                    </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {applied ? (
                    <Badge variant="default" className="text-[10px]">
                      <Check className="w-3 h-3 mr-1" />
                      Applied
                    </Badge>
                  ) : choosing ? (
                    <>
                      <span className="text-xs text-muted-foreground">
                        Pick which version to apply:
                      </span>
                      {cl.proposals.map((p) => {
                        const k = `panel-${turn.id}-${cl.key}-${p.agentId}`;
                        return (
                          <div
                            key={p.agentId}
                            className="flex items-center gap-1"
                          >
                            <Button
                              size="sm"
                              variant="default"
                              disabled={applyingKey === k}
                              onClick={() => onApply(turnIdx, cl.key, p)}
                              data-testid={`button-apply-choose-${turnIdx}-${cl.key}-${p.agentId}`}
                            >
                              {applyingKey === k ? (
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Check className="w-3 h-3 mr-1" />
                              )}
                              {p.agentName}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={applyingKey === k}
                              onClick={() => onPreview(turnIdx, cl.key, p)}
                              data-testid={`button-view-diff-choose-${turnIdx}-${cl.key}-${p.agentId}`}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View Diff
                            </Button>
                          </div>
                        );
                      })}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConflictChooser(null)}
                        data-testid={`button-cancel-chooser-${turnIdx}-${cl.key}`}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => {
                          if (cl.conflict && cl.proposals.length > 1) {
                            setConflictChooser({
                              turnIdx,
                              clusterKey: cl.key,
                            });
                          } else {
                            onApply(turnIdx, cl.key, cl.proposals[0]);
                          }
                        }}
                        disabled={
                          !!applyingKey &&
                          applyingKey.startsWith(
                            `panel-${turn.id}-${cl.key}-`,
                          )
                        }
                        data-testid={`button-apply-cluster-${turnIdx}-${cl.key}`}
                      >
                        <Check className="w-3 h-3 mr-1" />
                        {cl.conflict && cl.proposals.length > 1
                          ? "Apply (choose)…"
                          : `Apply (${cl.proposals[0].agentName})`}
                      </Button>
                      {!(cl.conflict && cl.proposals.length > 1) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            onPreview(turnIdx, cl.key, cl.proposals[0])
                          }
                          disabled={
                            !!applyingKey &&
                            applyingKey.startsWith(
                              `panel-${turn.id}-${cl.key}-`,
                            )
                          }
                          data-testid={`button-view-diff-cluster-${turnIdx}-${cl.key}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View Diff
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDismiss(turnIdx, cl.key)}
                        data-testid={`button-dismiss-cluster-${turnIdx}-${cl.key}`}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Dismiss
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
